/**
 * Centralized Theme Configuration
 * Updated with the palette:
 * 0D409D, 0096CD, 9CD320, D9E6C3, 3C4043, FCF8F3
 */

// ============================================================================
// RAW COLOR PALETTE
// ============================================================================
export const rawColors = {
  royal: '#0D409D',     // primary
  cyan: '#0096CD',      // secondary
  lime: '#9CD320',      // tertiary
  softGreen: '#D9E6C3', // surface alt
  graphite: '#3C4043',  // neutral dark
  cream: '#FCF8F3',     // background
  white: '#FFFFFF',
  black: '#000000',
  slate: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    900: '#0f172a',
  },
};

// ============================================================================
// SEMANTIC COLORS
// ============================================================================
export const colors = {
  primary: rawColors.cyan,
  primaryLight: `${rawColors.cyan}20`,
  primaryDark: `${rawColors.cyan}E6`,

  secondary: rawColors.cyan,
  secondaryLight: `${rawColors.cyan}20`,
  secondaryDark: `${rawColors.cyan}E6`,

  tertiary: rawColors.royal,
  tertiaryLight: `${rawColors.royal}20`,

  background: rawColors.graphite,
  backgroundLight: `${rawColors.graphite}90`,

  surface: rawColors.white,
  surfaceLight: rawColors.softGreen,

  text: rawColors.graphite,
  textLight: rawColors.slate[600],
  textLighter: rawColors.slate[500],
  textOnDark: rawColors.cyan,
  textOnDarkLight: `${rawColors.cyan}CC`,

  white: rawColors.white,
  black: rawColors.black,
  slate: rawColors.slate,
};

// ============================================================================
// GRADIENTS
// ============================================================================
export const gradients = {
  textPrimary: 'from-primary via-secondary to-primary',
  textSecondary: 'from-secondary via-tertiary to-secondary',
  bgPrimary: 'from-primary to-primary/80',
  bgSecondary: 'from-secondary to-secondary/80',
  bgHero: 'from-primary/90 via-secondary/90 to-tertiary/80',
  bgGlass: 'from-white via-slate-50 to-white',
  bgDark: 'from-graphite to-graphite/90',
  borderPrimary: 'from-primary via-secondary to-primary',
  shimmer: 'from-transparent via-white/20 to-transparent',
};

// ============================================================================
// SHADOWS
// ============================================================================
export const shadows = {
  glowPrimary: 'shadow-lg shadow-primary/30',
  glowSecondary: 'shadow-lg shadow-secondary/30',
  cardDefault: 'shadow-md shadow-slate-200/70',
  cardHover: 'shadow-lg shadow-slate-300/70',
  panelDefault: 'shadow-md shadow-slate-200/50',
  navbarDefault: 'shadow-lg shadow-primary/30',
};

// ============================================================================
// OPACITY VARIANTS
// ============================================================================
export const opacity = {
  primary: { 10: 'primary/10', 20: 'primary/20', 30: 'primary/30', 40: 'primary/40', 80: 'primary/80' },
  secondary: { 10: 'secondary/10', 20: 'secondary/20', 30: 'secondary/30', 40: 'secondary/40' },
  tertiary: { 10: 'tertiary/10', 20: 'tertiary/20' },
  background: { 70: 'background/70', 80: 'background/80', 85: 'background/85', 90: 'background/90' },
  surface: { 80: 'surface/80', 85: 'surface/85' },
  white: { 20: 'white/20', 40: 'white/40', 70: 'white/70' },
  black: { 70: 'black/70' },
  slate: { 50: 'slate-300/50', 70: 'slate-300/70' },
};

// ============================================================================
// CATEGORY COLORS
// ============================================================================
export const categoryColors = {
  Sustainability: 'from-secondary/15 to-transparent border-secondary/30 text-tertiary',
  Nutrition: 'from-primary/10 to-transparent border-primary/25 text-primary',
  Process: 'from-tertiary/10 to-transparent border-tertiary/25 text-tertiary',
  // Fallback for any future/unknown category
  default: 'from-primary/10 to-transparent border-primary/25 text-primary',
};

// ============================================================================
// BUTTON STYLES
// ============================================================================
export const buttonStyles = {
  primary:
    'rounded-full bg-gradient-to-r from-primary to-primary/80 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white transition-all hover:shadow-lg hover:shadow-primary/40 active:scale-95',
  secondary:
    'rounded-full border border-primary/40 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-primary transition-all hover:bg-primary/10 hover:shadow-lg hover:shadow-primary/20 active:scale-95',
  accent:
    'rounded-full bg-gradient-to-r from-secondary to-secondary/80 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white transition-all hover:shadow-lg hover:shadow-secondary/40 active:scale-95',
};

// ============================================================================
// COMPONENT STYLES
// ============================================================================
export const componentStyles = {
  glassPanel: 'bg-white/70 backdrop-blur-md shadow-lg border border-white/40',
  cardHover: 'transition-all duration-300 hover:shadow-2xl hover:shadow-slate-300/50',
};

// ============================================================================
// EFFECTS, ANIMATIONS, SPACING, ETC.
// ============================================================================
export const effects = {
  blurMd: 'backdrop-blur-md',
  blurXl: 'backdrop-blur-xl',
  blur2xl: 'backdrop-blur-2xl',
  saturate: 'saturate(180%)',
};

export const animations = {
  shimmer: { '0%': { transform: 'translateX(-100%)' }, '100%': { transform: 'translateX(100%)' } },
  fadeIn: { '0%': { opacity: 0, transform: 'translateY(12px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
  float: { '0%, 100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-12px)' } },
};

export const spacing = {
  navbarPadding: { x: '16px', y: '12px' },
  panelPadding: { x: '16px', y: '16px' },
};

export const borderRadius = {
  sm: '12px',
  md: '28px',
  lg: '32px',
  full: '9999px',
};

export const fonts = {
  display: ['Poppins', 'ui-sans-serif', 'system-ui'],
  body: ['Inter', 'ui-sans-serif', 'system-ui'],
};

// ============================================================================
// THEME EXPORT
// ============================================================================
export const theme = {
  colors,
  gradients,
  shadows,
  opacity,
  categoryColors,
  buttonStyles,
  componentStyles,
  effects,
  animations,
  spacing,
  borderRadius,
  fonts,
};

export default theme;
